import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Box, ChevronLeft } from 'lucide-react';
import { useAppDispatch } from '@/store/store';
import { setQueue, playVideo, addToQueue } from '@/features/player/playerSlice';
import VideoTable from '@/components/table/VideoTable';
import { analyzeVideo } from '@/services/audio.api';
import { Button } from '@/components/ui';
import {
  Crate,
  CrateVideo,
  CRATE_MAX_SLOTS,
  getCrate,
  removeVideoFromCrate,
} from '@/services/crates.api';

const CrateDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const [crate, setCrate] = useState<Crate | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadCrate = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      setCrate(await getCrate(id));
    } catch {
      console.error('Failed to load crate');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadCrate();
  }, [loadCrate]);

  const crateVideos = useMemo<CrateVideo[]>(() => crate?.videos ?? [], [crate]);

  const videos = useMemo(
    () =>
      crateVideos.map(video => ({
        ...video,
        channelId: video.channel?.channelId || video.channel?.id || '',
        publishedAt: new Date(video.publishedAt),
        dateAdded: new Date(video.publishedAt),
        createdAt: new Date(video.publishedAt),
        updatedAt: new Date(video.publishedAt),
      })) as any[],
    [crateVideos]
  );

  const audioFeatures = useMemo(() => {
    const map: Record<string, any> = {};
    crateVideos.forEach(video => {
      if (video.audioFeatures) map[video.videoId] = video.audioFeatures;
    });
    return map;
  }, [crateVideos]);

  const handlePlayVideo = (video: any) => {
    dispatch(setQueue(videos));
    dispatch(playVideo(video));
  };

  const handleAddToQueue = (video: any) => {
    dispatch(addToQueue(video));
  };

  const handleAnalyze = async (video: any) => {
    try {
      await analyzeVideo(video.videoId);
    } catch {
      console.error('Failed to queue analysis');
    }
  };

  const handleRemove = async (video: any) => {
    if (!id) return;
    try {
      const updated = await removeVideoFromCrate(id, video.videoId);
      setCrate(updated);
    } catch {
      console.error('Failed to remove from crate');
    }
  };

  if (isLoading) {
    return (
      <div className="p-0 pt-4 md:p-6 max-w-7xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-48 bg-neutral-300 dark:bg-bg-dark-tertiary rounded mb-6" />
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-20 bg-neutral-200 dark:bg-bg-dark-secondary rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (!crate) {
    return (
      <div className="p-6 max-w-7xl mx-auto text-center py-16">
        <Box className="w-16 h-16 text-neutral-300 dark:text-text-dark-tertiary mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-meradio-dark dark:text-text-dark-primary mb-4">
          Crate not found
        </h1>
        <Link to="/crates">
          <Button variant="primary">Back to crates</Button>
        </Link>
      </div>
    );
  }

  const count = crate.videoCount ?? crateVideos.length;
  const isFull = count >= CRATE_MAX_SLOTS;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <Link
        to="/crates"
        className="mx-4 md:mx-0 inline-flex items-center gap-1 text-sm text-meradio-primary hover:text-meradio-primary-dark mb-4"
      >
        <ChevronLeft className="w-4 h-4" />
        Crates
      </Link>

      <div className="px-4 md:px-0 flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-meradio-primary/10 flex items-center justify-center">
            <Box className="w-6 h-6 text-meradio-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-meradio-dark dark:text-text-dark-primary">
              {crate.name}
            </h1>
            <p className="text-sm text-meradio-dark-light dark:text-text-dark-secondary tabular-nums">
              {count}/{CRATE_MAX_SLOTS} records
            </p>
          </div>
        </div>
      </div>

      {isFull && (
        <div className="mb-4 rounded-lg border border-danger-200 dark:border-danger-500/40 bg-danger-50 dark:bg-danger-500/10 px-4 py-3 text-sm text-danger-700 dark:text-danger-400">
          Crate full — pull something out first before adding more.
        </div>
      )}

      {crateVideos.length === 0 ? (
        <div className="text-center py-16">
          <Box className="w-16 h-16 text-neutral-300 dark:text-text-dark-tertiary mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-neutral-600 dark:text-text-dark-primary mb-2">
            This crate is empty
          </h3>
          <p className="text-neutral-500 dark:text-text-dark-secondary">
            Add records from your videos using the folder action.
          </p>
        </div>
      ) : (
        <VideoTable
          videos={videos}
          audioFeatures={audioFeatures}
          onPlayVideo={handlePlayVideo}
          onAddToQueue={handleAddToQueue}
          onAnalyze={handleAnalyze}
          onRemoveFromCrate={handleRemove}
        />
      )}
    </div>
  );
};

export default CrateDetail;
